/**
 * Interpolação `{{variavel}}` — usada em textos, prompts, URLs, headers e bodies.
 *
 * Aceita caminho com ponto (`{{resposta.cliente.nome}}`), porque resposta de webhook chega
 * como objeto. O acesso é apenas a propriedades próprias e `__proto__`/`constructor`/`prototype`
 * são recusados de saída — a config vem de um editor, não de código confiável.
 *
 * `{{secret:nome}}` é resolvido **somente** quando um mapa de segredos é passado, o que
 * acontece exclusivamente dentro da Cloud Function. No browser o token fica intocado — e
 * como a config pública não carrega o body do webhook de backend, ele nunca chega lá.
 */
const TOKEN = /\{\{\s*([^}]*?)\s*\}\}/g;
const BLOCKED_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
export const SECRET_PREFIX = 'secret:';
/** Lê `a.b.0.c` em variáveis aninhadas. Devolve `undefined` se o caminho não existir. */
export function getVariablePath(variables, path) {
    const parts = path.split('.').map((p) => p.trim());
    if (parts.some((p) => p.length === 0 || BLOCKED_KEYS.has(p)))
        return undefined;
    let current = variables;
    for (const part of parts) {
        if (current === null || current === undefined)
            return undefined;
        if (Array.isArray(current)) {
            const index = Number(part);
            if (!Number.isInteger(index) || index < 0 || index >= current.length)
                return undefined;
            current = current[index];
            continue;
        }
        if (typeof current !== 'object')
            return undefined;
        if (!Object.prototype.hasOwnProperty.call(current, part))
            return undefined;
        current = current[part];
    }
    return current;
}
/** Como um valor aparece dentro de um texto: objeto vira JSON, `null`/ausente vira string vazia. */
export function stringifyValue(value) {
    if (value === undefined || value === null)
        return '';
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);
    try {
        return JSON.stringify(value);
    }
    catch {
        return '';
    }
}
export function interpolate(template, variables, options = {}) {
    if (!template)
        return '';
    return template.replace(TOKEN, (match, rawToken) => {
        const token = rawToken.trim();
        if (!token)
            return '';
        if (token.startsWith(SECRET_PREFIX)) {
            const name = token.slice(SECRET_PREFIX.length).trim();
            if (!options.secrets)
                return match; // contexto do browser: não é nosso para resolver
            const secret = options.secrets[name];
            if (secret === undefined) {
                options.missing?.push(token);
                return '';
            }
            return secret;
        }
        const value = getVariablePath(variables, token);
        if (value === undefined)
            options.missing?.push(token);
        return stringifyValue(value);
    });
}
/** Interpola recursivamente strings dentro de objetos/arrays (headers, por exemplo). */
export function interpolateDeep(input, variables, options = {}) {
    if (typeof input === 'string')
        return interpolate(input, variables, options);
    if (Array.isArray(input)) {
        return input.map((item) => interpolateDeep(item, variables, options));
    }
    if (input && typeof input === 'object') {
        const out = {};
        for (const [key, value] of Object.entries(input)) {
            out[key] = interpolateDeep(value, variables, options);
        }
        return out;
    }
    return input;
}
/** Tokens usados por um template — o painel mostra quais variáveis um bloco consome. */
export function extractTokens(template) {
    const tokens = [];
    for (const match of template.matchAll(TOKEN)) {
        const token = match[1]?.trim();
        if (token)
            tokens.push(token);
    }
    return tokens;
}
//# sourceMappingURL=interpolate.js.map